import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { assignShipment, claimShipment, getOwnershipEvents, releaseShipment } from '../../api/endpoints/ops';
import { DnmRequestFailure } from '../../api/dnmFailure';
import { historyQueryPolicy } from '../../api/queryPolicies';
import type { OwnershipAction, OwnershipEvent, ShipmentResponse } from '../../api/types';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useMutationHook } from '../../hooks/useMutationHook';
import { OwnerChip } from './OwnerChip';
import { ReassignModal } from './ReassignModal';
import {
  isOpenShipment,
  OWNERSHIP_INVALIDATE_KEYS,
  ownershipEventsKey,
  opsUserName,
  REASON_MAX,
  REASON_MIN,
  useOpsUser,
  useOpsUsers,
} from './opsUser';
import { ownershipErrorMessage } from './ownershipErrors';
import styles from './OwnershipPanel.module.css';
import { Button } from '../../components/ui/Button';

const ACTION_LABEL: Record<OwnershipAction, string> = {
  CLAIM: 'Claimed',
  ASSIGN: 'Assigned',
  REASSIGN: 'Reassigned',
  RELEASE: 'Released',
  AUTO_ASSIGN: 'Auto-assigned',
};

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function describeEvent(event: OwnershipEvent): string {
  const to = event.to ? opsUserName(event.to) : null;
  const from = event.from ? opsUserName(event.from) : null;
  const by = event.by ? opsUserName(event.by) : 'System';
  switch (event.action) {
    case 'CLAIM':
      return `${to ?? by} claimed it`;
    case 'AUTO_ASSIGN':
      return `Auto-assigned to ${to ?? '—'} (account manager / creator)`;
    case 'ASSIGN':
      return `${by} assigned it to ${to ?? '—'}`;
    case 'REASSIGN':
      return `${by} moved it from ${from ?? '—'} to ${to ?? '—'}`;
    case 'RELEASE':
      return `${by} released it${from && from !== by ? ` from ${from}` : ''}`;
  }
}

/**
 * Shipment owner panel: current owner + since, Claim / Release / (Re)assign,
 * and the ownership history. Non-owners can still perform every other admin
 * action — ownership only routes responsibility.
 */
export function OwnershipPanel({ shipment }: { shipment: ShipmentResponse }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { opsUser, isManager } = useOpsUser();
  const opsUsers = useOpsUsers(isManager);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const owner = shipment.owner ?? null;
  const open = isOpenShipment(shipment.status);
  const isOwner = !!owner && !!opsUser && owner.id === opsUser.id;

  const events = useQuery({
    queryKey: ownershipEventsKey(shipment.id),
    queryFn: () => getOwnershipEvents(shipment.id),
    enabled: historyOpen,
    ...historyQueryPolicy,
  });

  const refreshAfterConflict = (error: Error) => {
    if (error instanceof DnmRequestFailure && error.response?.status === 409) {
      OWNERSHIP_INVALIDATE_KEYS.forEach((key) => void queryClient.invalidateQueries({ queryKey: key }));
    }
  };

  const claim = useMutationHook(() => claimShipment(shipment.id), {
    successMessage: 'Shipment claimed — it’s now in My Work.',
    errorMessage: ownershipErrorMessage,
    invalidateKeys: OWNERSHIP_INVALIDATE_KEYS,
    onSuccess: () => setPanelError(null),
    onError: (error) => {
      setPanelError(ownershipErrorMessage(error));
      refreshAfterConflict(error);
    },
  });

  const release = useMutationHook((reason: string) => releaseShipment(shipment.id, reason), {
    successMessage: 'Shipment released to the Unassigned queue.',
    errorMessage: ownershipErrorMessage,
    invalidateKeys: OWNERSHIP_INVALIDATE_KEYS,
    onSuccess: () => {
      setReleaseOpen(false);
      setPanelError(null);
    },
    onError: (error) => {
      setDialogError(ownershipErrorMessage(error));
      refreshAfterConflict(error);
    },
  });

  const assign = useMutationHook(
    ({ opsUserId, reason }: { opsUserId: number; reason: string }) =>
      assignShipment(shipment.id, opsUserId, reason),
    {
      successMessage: (s) => (s.owner ? `Assigned to ${opsUserName(s.owner)}.` : 'Shipment assigned.'),
      errorMessage: ownershipErrorMessage,
      invalidateKeys: OWNERSHIP_INVALIDATE_KEYS,
      onSuccess: () => {
        setAssignOpen(false);
        setPanelError(null);
      },
      onError: (error) => {
        setDialogError(ownershipErrorMessage(error));
        refreshAfterConflict(error);
      },
    },
  );

  const canClaim = open && !owner;
  const canRelease = !!owner && (isOwner || isManager);
  const canAssign = open && isManager;

  return (
    <section className={styles.panel} aria-label={t('ownership.ownershipPanel.ariaLabel')}>
      <div className={styles.row}>
        <span className={styles.label}>{t('ownership.ownershipPanel.owner')}</span>
        <div className={styles.owner}>
          <OwnerChip owner={owner} meId={opsUser?.id} />
          {owner && shipment.ownedSince && (
            <span className={styles.since}>{t('ownership.ownershipPanel.since', { date: fmtDateTime(shipment.ownedSince) })}</span>
          )}
        </div>
        <div className={styles.actions}>
          {canClaim && (
            <Button
              type="button"
              variant="primary" size="sm"
              disabled={claim.isLoading}
              onClick={() => claim.mutate(undefined)}
            >
              {claim.isLoading ? 'Claiming…' : 'Claim'}
            </Button>
          )}
          {canRelease && (
            <Button
              type="button"
              variant="secondary" size="sm"
              onClick={() => {
                setDialogError(null);
                setReleaseOpen(true);
              }}
            >
              {t('ownership.ownershipPanel.release')}
            </Button>
          )}
          {canAssign && (
            <Button
              type="button"
              variant="secondary" size="sm"
              onClick={() => {
                setDialogError(null);
                setAssignOpen(true);
              }}
            >
              {owner ? 'Reassign' : 'Assign'}
            </Button>
          )}
        </div>
      </div>

      {panelError && (
        <div className={styles.error} role="alert">
          {panelError}
        </div>
      )}

      <button
        type="button"
        className={styles.historyToggle}
        aria-expanded={historyOpen}
        onClick={() => setHistoryOpen((v) => !v)}
      >
        {historyOpen ? 'Hide ownership history' : 'Show ownership history'}
      </button>

      {historyOpen && (
        <ul className={styles.history} aria-label={t('ownership.ownershipPanel.historyLabel')}>
          {events.isLoading && <li className={styles.empty}>{t('ownership.ownershipPanel.historyLoading')}</li>}
          {events.isError && <li className={styles.empty}>{t('ownership.ownershipPanel.historyError')}</li>}
          {events.data?.length === 0 && <li className={styles.empty}>{t('ownership.ownershipPanel.historyEmpty')}</li>}
          {events.data?.map((event) => (
            <li key={event.id} className={styles.event}>
              <span className={styles.action}>{ACTION_LABEL[event.action]}</span>
              <span>
                {describeEvent(event)}
                {event.reason && <span className={styles.reason}>“{event.reason}”</span>}
              </span>
              <span className={styles.when}>{fmtDateTime(event.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        isOpen={releaseOpen}
        title={t('ownership.ownershipPanel.releaseTitle')}
        description={
          owner
            ? `${isOwner ? 'You' : opsUserName(owner)} will no longer own this shipment; it returns to the Unassigned queue.`
            : ''
        }
        confirmText="Release"
        variant="danger"
        help="release-shipment"
        requireReason
        reasonMinLength={REASON_MIN}
        reasonMaxLength={REASON_MAX}
        reasonPlaceholder="Why are you releasing it? (3–500 characters)"
        loading={release.isLoading}
        errorMessage={dialogError}
        onCancel={() => setReleaseOpen(false)}
        onConfirm={(reason) => release.mutate(reason ?? '')}
      />

      {assignOpen && (
        <ReassignModal
          currentOwner={owner}
          opsUsers={opsUsers.data ?? []}
          loading={assign.isLoading}
          errorMessage={dialogError}
          onCancel={() => setAssignOpen(false)}
          onConfirm={(opsUserId, reason) => assign.mutate({ opsUserId, reason })}
        />
      )}
    </section>
  );
}
